import { useState, useEffect } from 'react';
import { applyPageMeta, SITE_URL } from '@/lib/pageMeta';
import Navbar from '@/pages/home/components/Navbar';
import Footer from '@/pages/home/components/Footer';

const stats = [
  { value: '500+', label: 'Casino Games' },
  { value: '1000+', label: 'Sports Events Daily' },
  { value: '24/7', label: 'Customer Support' },
  { value: '128-bit', label: 'SSL Encryption' },
];

const gameCategories = [
  {
    icon: 'ri-gamepad-line',
    title: 'Online Slots',
    desc: 'Hundreds of slot titles from studios such as BNG, JILI, CQ9 Gaming, JOKER, Microgaming, FC FA CHAI, KINGMAKER, Fast Spin, and many more. From classic reels to modern video slots with bonuses, free spins, and big-win potential.',
  },
  {
    icon: 'ri-live-line',
    title: 'Live Casino',
    desc: 'Experience the thrill of a real casino from the comfort of your home. Our live dealer tables feature Baccarat, Roulette, Blackjack, Dragon Tiger, and Sic Bo — streamed in HD 24/7.',
  },
  {
    icon: 'ri-football-line',
    title: 'Sports Betting',
    desc: 'Bet on thousands of sporting events every day — football, basketball, tennis, rugby, cricket, and more. Competitive odds, live in-play betting, and same-game parlays available.',
  },
];

/** Game providers shown in PNGGO partner grids (merged from brand asset screenshots). */
const providers = [
  'BNG',
  'WF Gaming',
  'WF Gaming 2',
  'META GAMING',
  'SCR888',
  'SCR888 H5',
  'JILI',
  'VPOWER',
  '918Kaya',
  'MEGAH5',
  'IMPERIUM-GAMES',
  'ADVANTPLAY',
  'UU SLOTS',
  'JDB',
  'Live22',
  '888KING',
  'CQ9 GAMING',
  'RICH GAMING',
  'PEGASUS',
  'Lucky365',
  'JOKER',
  'i-Bex Game Soft',
  'Acewin',
  'MARIO CLUB',
  'BT GAMING',
  'KINGMAKER',
  'FAST SPIN',
  'FC FACHAI',
  'CLOTPLAY',
  'APOLLO 777',
  'DRAGOON SOFT',
  'XE88',
  'AI GAMING',
  'BE SOFT',
  'Crowd Play',
  'CG CREATIVE GAMING',
  'Microgaming',
  'EVO888H5',
  'Slotomania',
];

const securityFeatures = [
  {
    icon: 'ri-shield-check-line',
    title: '128-Bit SSL Encryption',
    desc: 'All data transmitted between you and PNGGO is protected by military-grade 128-bit SSL encryption, ensuring your personal and financial information is always safe.',
  },
  {
    icon: 'ri-lock-password-line',
    title: 'Secure Account Protection',
    desc: 'Two-factor authentication, login alerts, and session management tools give you full control over your account security at all times.',
  },
  {
    icon: 'ri-bank-card-line',
    title: 'Safe & Fast Transactions',
    desc: 'Deposits and withdrawals are processed through verified payment gateways. Funds are segregated and protected, with most withdrawals completed within minutes.',
  },
  {
    icon: 'ri-eye-off-line',
    title: 'Privacy Guaranteed',
    desc: 'We never share your personal data with third parties. Your identity and gaming history remain strictly confidential under our privacy policy.',
  },
];

const responsibleItems = [
  { icon: 'ri-time-line', title: 'Deposit Limits', desc: 'Set daily, weekly, or monthly deposit caps to keep your spending in check.' },
  { icon: 'ri-user-forbid-line', title: 'Self-Exclusion', desc: 'Temporarily or permanently exclude yourself from the platform at any time.' },
  { icon: 'ri-alarm-warning-line', title: 'Reality Checks', desc: 'Receive periodic reminders of how long you have been playing.' },
  { icon: 'ri-customer-service-2-line', title: 'Support Resources', desc: 'Access to professional counselling and gambling support organisations in PNG.' },
];

const timeline = [
  { year: '2018', title: 'Founded', desc: 'PNGGO was established with a vision to bring world-class online gaming to Papua New Guinea.' },
  { year: '2019', title: 'Licensed', desc: 'Obtained full gaming licence, becoming one of the first legally operating online casinos in PNG.' },
  { year: '2020', title: 'Platform Launch', desc: 'Launched the full platform with 200+ games, sports betting, and live casino offerings.' },
  { year: '2021', title: 'Mobile App', desc: 'Released iOS and Android apps, bringing PNGGO to players\' fingertips anywhere in PNG.' },
  { year: '2022', title: '100K Players', desc: 'Reached 100,000 registered players milestone, cementing our position as PNG\'s top casino.' },
  { year: '2023', title: 'Expansion', desc: 'Expanded game library to 500+ titles and added new payment methods including local PNG banking.' },
  { year: '2024', title: 'Innovation', desc: 'Sharpened the product around PNG players — deeper slots catalog, richer live casino, and smoother sports betting in PGK.' },
];

const paymentMethods = [
  { icon: 'ri-bank-line', name: 'Kina Bank' },
  { icon: 'ri-bank-line', name: 'BSP' },
];

const ABOUT_TITLE = "About PNGGO – Papua New Guinea's Premier Online Casino";
const ABOUT_DESCRIPTION =
  "Learn about PNGGO, Papua New Guinea's most trusted licensed online casino. Discover our mission, vision, game categories including slots, live casino, sports betting, and our commitment to PNG players.";
const ABOUT_KEYWORDS =
  'PNGGO about, Papua New Guinea licensed casino, PNG online casino company, PNGGO mission, PNG slots live casino';

const aboutJsonLd = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "AboutPage",
      "@id": `${SITE_URL}/about#webpage`,
      "url": `${SITE_URL}/about`,
      "name": ABOUT_TITLE,
      "description": ABOUT_DESCRIPTION,
      "inLanguage": "en",
      "isPartOf": {
        "@id": `${SITE_URL}/#website`
      }
    },
    {
      "@type": "Organization",
      "@id": `${SITE_URL}/#organization`,
      "name": "PNGGO",
      "url": SITE_URL,
      "foundingDate": "2018",
      "description": "PNGGO is Papua New Guinea's premier licensed online casino and sports betting platform, offering slots, live casino, and sports betting exclusively for PNG players.",
      "areaServed": {
        "@type": "Country",
        "name": "Papua New Guinea"
      },
      "numberOfEmployees": {
        "@type": "QuantitativeValue",
        "value": "50+"
      }
    }
  ]
};

function SectionBadge({ text }: { text: string }) {
  return (
    <span className="inline-block bg-[#B80E28]/15 text-[#B80E28] text-xs font-bold tracking-widest uppercase px-4 py-1.5 rounded-full mb-4">
      {text}
    </span>
  );
}

export default function AboutPage() {
  const [activeTab, setActiveTab] = useState<'mission' | 'vision' | 'values'>('mission');

  useEffect(() => {
    applyPageMeta({
      title: ABOUT_TITLE,
      description: ABOUT_DESCRIPTION,
      path: '/about',
      keywords: ABOUT_KEYWORDS,
      ogDescription: ABOUT_DESCRIPTION,
      twitterDescription: ABOUT_DESCRIPTION,
    });
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'about-jsonld';
    script.text = JSON.stringify(aboutJsonLd);
    document.head.appendChild(script);
    return () => {
      const el = document.getElementById('about-jsonld');
      if (el) el.remove();
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#0a0a0a] pnggo-page-motif">
      <Navbar />

      {/* ── Hero ── */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: "url('https://readdy.ai/api/search-image?query=Papua%20New%20Guinea%20tropical%20landscape%20aerial%20view%20lush%20green%20mountains%20coastline%20dramatic%20sky%20cinematic%20wide%20angle%20photography%20vibrant%20colors%20rich%20jungle%20scenery&width=1440&height=600&seq=about-hero-01&orientation=landscape')" }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/75 via-black/60 to-[#0a0a0a]" />
        <div className="relative max-w-7xl mx-auto px-6 text-center">
          <SectionBadge text="About PNGGO" />
          <h1 className="text-white text-4xl md:text-6xl font-extrabold tracking-tight mb-6 leading-tight">
            Papua New Guinea&apos;s<br />
            <span className="text-[#B80E28]">Premier Online Casino</span>
          </h1>
          <p className="text-white/65 text-base md:text-lg max-w-2xl mx-auto leading-7 mb-10">
            PNGGO is the most trusted, licensed, and feature-rich online casino platform built exclusively for players in Papua New Guinea. We combine cutting-edge technology with a passion for entertainment to deliver an unmatched gaming experience.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a
              href="https://pnggo.bet/RFLANDINGPAGEPNG"
              className="inline-flex items-center gap-2 bg-[#B80E28] text-white font-bold px-8 py-3 rounded-full hover:bg-[#9a0b21] transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              Join PNGGO Now <i className="fas fa-chevron-right text-xs"></i>
            </a>
            <a
              href="https://pnggo.bet/RFLANDINGPAGEPNG"
              className="inline-flex items-center gap-2 border border-white/20 text-white font-semibold px-8 py-3 rounded-full hover:border-[#B80E28] hover:text-[#B80E28] transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              Explore Games
            </a>
          </div>
        </div>
      </section>

      {/* ── Stats Bar ── */}
      <section className="bg-[#111] border-y border-white/5 py-10">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-2 md:grid-cols-4 gap-8">
          {stats.map((s, i) => (
            <div key={i} className="text-center">
              <div className="text-[#B80E28] text-3xl md:text-4xl font-extrabold mb-1">{s.value}</div>
              <div className="text-white/50 text-sm">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ── Who We Are ── */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
            <div>
              <SectionBadge text="Who We Are" />
              <h2 className="text-white text-3xl md:text-4xl font-bold mb-6 leading-tight">
                Built for PNG Players,<br />
                <span className="text-[#B80E28]">By Gaming Experts</span>
              </h2>
              <p className="text-white/60 text-sm leading-7 mb-5">
                PNGGO was founded with a single purpose: to give players in Papua New Guinea access to a world-class online gaming platform that is safe, legal, and genuinely exciting. We are fully licensed and regulated, operating under strict compliance standards to protect every player.
              </p>
              <p className="text-white/60 text-sm leading-7 mb-5">
                Our team of gaming professionals, security engineers, and customer care specialists work around the clock to ensure that every session on PNGGO is smooth, fair, and rewarding. From the moment you register to your first withdrawal, we are with you every step of the way.
              </p>
              <p className="text-white/60 text-sm leading-7 mb-8">
                We partner with the world&apos;s leading game providers to bring you the freshest titles, the most competitive odds, and the most immersive live casino experience available in PNG. PNGGO is not just a casino — it is your complete entertainment destination.
              </p>
              <div className="flex flex-wrap gap-3">
                {['Licensed & Regulated', 'Fair Play Certified', 'Responsible Gaming', 'PNG-Focused'].map((tag) => (
                  <span key={tag} className="bg-[#1a1a1a] border border-[#B80E28]/30 text-[#B80E28] text-xs font-semibold px-4 py-1.5 rounded-full">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
            <div className="relative">
              <img
                src="https://readdy.ai/api/search-image?query=modern%20online%20casino%20gaming%20interface%20dark%20theme%20luxury%20premium%20digital%20entertainment%20platform%20glowing%20screens%20neon%20red%20accents%20professional%20studio%20photography&width=600&height=500&seq=about-whoweare-01&orientation=landscape"
                alt="PNGGO Online Casino Platform"
                className="w-full h-[380px] object-cover object-top rounded-2xl"
              />
              <div className="absolute -bottom-5 -left-5 bg-[#B80E28] text-white rounded-xl px-6 py-4">
                <div className="text-2xl font-extrabold">6+ Years</div>
                <div className="text-xs text-white/80">Serving PNG Players</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Mission / Vision / Values Tabs ── */}
      <section className="bg-[#0d0d0d] py-20">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-10">
            <SectionBadge text="Our Foundation" />
            <h2 className="text-white text-3xl md:text-4xl font-bold">Mission, Vision & Values</h2>
          </div>
          {/* Tabs */}
          <div className="flex justify-center mb-8">
            <div className="bg-[#1a1a1a] rounded-full p-1 flex gap-1">
              {(['mission', 'vision', 'values'] as const).map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`px-6 py-2 rounded-full text-sm font-semibold transition-all duration-300 cursor-pointer whitespace-nowrap capitalize ${
                    activeTab === tab
                      ? 'bg-[#B80E28] text-white'
                      : 'text-white/50 hover:text-white'
                  }`}
                >
                  {tab}
                </button>
              ))}
            </div>
          </div>
          {/* Tab Content */}
          <div className="bg-[#1a1a1a] rounded-2xl p-10 border border-white/5 min-h-[220px]">
            {activeTab === 'mission' && (
              <div>
                <div className="flex items-center gap-3 mb-5">
                  <span className="w-1.5 h-8 bg-[#B80E28] rounded-full"></span>
                  <h3 className="text-white text-xl font-bold">Our Mission</h3>
                </div>
                <p className="text-white/60 text-sm leading-8 mb-4">
                  PNGGO&apos;s mission is to provide a premier casino online platform that prioritises responsible gaming in Papua New Guinea. We aim to offer a safe and secure environment for our users, promoting responsible gambling practices and advocating for player well-being.
                </p>
                <p className="text-white/60 text-sm leading-8">
                  Our commitment extends beyond entertainment to fostering a culture of responsible gaming, where users can enjoy the thrill of betting while maintaining control over their gaming habits. At PNGGO, we continuously strive to uphold the highest standards of integrity, transparency, and player protection — because your trust is our most valuable asset.
                </p>
              </div>
            )}
            {activeTab === 'vision' && (
              <div>
                <div className="flex items-center gap-3 mb-5">
                  <span className="w-1.5 h-8 bg-[#B80E28] rounded-full"></span>
                  <h3 className="text-white text-xl font-bold">Our Vision</h3>
                </div>
                <p className="text-white/60 text-sm leading-8 mb-4">
                  PNGGO&apos;s vision is to be the leading legal gambling platform in Papua New Guinea, offering unparalleled excitement and innovation to players across the nation. We strive to set new standards of excellence in the industry by providing cutting-edge technology, diverse betting options, and exceptional customer service.
                </p>
                <p className="text-white/60 text-sm leading-8">
                  Our vision encompasses creating a dynamic and engaging betting experience that caters to the evolving needs and preferences of sports and casino enthusiasts in PNG. We aim to continuously innovate, adapt to emerging trends, and expand our offerings so that every player in Papua New Guinea has access to the very best online gaming experience available anywhere in the world.
                </p>
              </div>
            )}
            {activeTab === 'values' && (
              <div>
                <div className="flex items-center gap-3 mb-6">
                  <span className="w-1.5 h-8 bg-[#B80E28] rounded-full"></span>
                  <h3 className="text-white text-xl font-bold">Our Core Values</h3>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  {[
                    { icon: 'ri-shield-star-line', title: 'Integrity', desc: 'We operate with full transparency and honesty in everything we do.' },
                    { icon: 'ri-heart-pulse-line', title: 'Player First', desc: 'Every decision we make puts the safety and enjoyment of our players first.' },
                    { icon: 'ri-lightbulb-line', title: 'Innovation', desc: 'We constantly push boundaries to deliver the latest and greatest gaming experiences.' },
                    { icon: 'ri-community-line', title: 'Community', desc: 'We are proud to be part of the Papua New Guinea community and give back where we can.' },
                  ].map((v, i) => (
                    <div key={i} className="flex items-start gap-4">
                      <div className="w-10 h-10 flex items-center justify-center bg-[#B80E28]/15 rounded-lg flex-shrink-0">
                        <i className={`${v.icon} text-[#B80E28] text-lg`}></i>
                      </div>
                      <div>
                        <div className="text-white text-sm font-semibold mb-1">{v.title}</div>
                        <div className="text-white/50 text-xs leading-5">{v.desc}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ── Game Categories ── */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <SectionBadge text="What We Offer" />
            <h2 className="text-white text-3xl md:text-4xl font-bold mb-4">Complete Gaming Universe</h2>
            <p className="text-white/50 text-sm max-w-xl mx-auto">
              Slots, live casino, and sports — three pillars built for Papua New Guinea, with local banking and support that feels like home.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {gameCategories.map((cat, i) => (
              <div
                key={i}
                className="bg-[#111] border border-white/5 hover:border-[#B80E28]/40 rounded-xl p-7 transition-all duration-300 group"
              >
                <div className="w-12 h-12 flex items-center justify-center bg-[#B80E28]/15 rounded-xl mb-5 group-hover:bg-[#B80E28]/25 transition-colors">
                  <i className={`${cat.icon} text-[#B80E28] text-2xl`}></i>
                </div>
                <h3 className="text-white font-bold text-base mb-3">{cat.title}</h3>
                <p className="text-white/50 text-sm leading-6">{cat.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Game Providers ── */}
      <section className="bg-[#0d0d0d] py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-10">
            <SectionBadge text="Our Partners" />
            <h2 className="text-white text-3xl font-bold mb-3">World-Class Game Providers</h2>
            <p className="text-white/50 text-sm max-w-lg mx-auto">
              We partner exclusively with certified, internationally recognised game studios to guarantee fair, high-quality gameplay.
            </p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
            {providers.map((p, i) => (
              <div
                key={`${p}-${i}`}
                className="bg-[#1a1a1a] border border-white/5 hover:border-[#B80E28]/30 rounded-lg px-3 py-4 text-center transition-all duration-300 cursor-pointer"
              >
                <div className="w-8 h-8 flex items-center justify-center mx-auto mb-2">
                  <i className="ri-gamepad-fill text-[#B80E28] text-xl"></i>
                </div>
                <span className="text-white/70 text-xs font-medium">{p}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Security ── */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
            <div className="relative">
              <img
                src="https://readdy.ai/api/search-image?query=cybersecurity%20digital%20protection%20shield%20glowing%20red%20neon%20dark%20background%20data%20encryption%20secure%20server%20technology%20abstract%20visualization%20professional&width=600&height=500&seq=about-security-01&orientation=landscape"
                alt="PNGGO Security"
                className="w-full h-[380px] object-cover object-top rounded-2xl"
              />
              <div className="absolute top-5 right-5 bg-[#0a0a0a]/90 border border-[#B80E28]/40 rounded-xl px-5 py-4 text-center">
                <i className="ri-shield-check-fill text-[#B80E28] text-3xl"></i>
                <div className="text-white text-sm font-bold mt-1">100% Secure</div>
              </div>
            </div>
            <div>
              <SectionBadge text="Security & Trust" />
              <h2 className="text-white text-3xl md:text-4xl font-bold mb-6 leading-tight">
                Your Safety Is Our<br />
                <span className="text-[#B80E28]">Top Priority</span>
              </h2>
              <p className="text-white/60 text-sm leading-7 mb-8">
                PNGGO employs enterprise-grade security infrastructure to protect every player. Our platform is built on a foundation of trust, with multiple layers of protection ensuring your data, funds, and identity are always secure.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {securityFeatures.map((f, i) => (
                  <div key={i} className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center bg-[#B80E28]/15 rounded-lg flex-shrink-0">
                      <i className={`${f.icon} text-[#B80E28] text-lg`}></i>
                    </div>
                    <div>
                      <div className="text-white text-sm font-semibold mb-1">{f.title}</div>
                      <div className="text-white/50 text-xs leading-5">{f.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Our Journey / Timeline ── */}
      <section className="bg-[#0d0d0d] py-20">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-14">
            <SectionBadge text="Our Journey" />
            <h2 className="text-white text-3xl md:text-4xl font-bold">PNGGO Through the Years</h2>
          </div>
          <div className="relative">
            {/* Vertical line */}
            <div className="absolute left-1/2 -translate-x-1/2 top-0 bottom-0 w-px bg-white/10 hidden md:block"></div>
            <div className="flex flex-col gap-8">
              {timeline.map((item, i) => (
                <div
                  key={i}
                  className={`flex flex-col md:flex-row items-start md:items-center gap-4 md:gap-8 ${
                    i % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                  }`}
                >
                  <div className={`flex-1 ${i % 2 === 0 ? 'md:text-right' : 'md:text-left'}`}>
                    <div className="bg-[#1a1a1a] border border-white/5 hover:border-[#B80E28]/30 rounded-xl p-5 transition-all duration-300">
                      <div className="text-[#B80E28] text-xs font-bold tracking-widest mb-1">{item.year}</div>
                      <div className="text-white font-bold text-sm mb-2">{item.title}</div>
                      <div className="text-white/50 text-xs leading-5">{item.desc}</div>
                    </div>
                  </div>
                  {/* Dot */}
                  <div className="hidden md:flex w-4 h-4 flex-shrink-0 items-center justify-center">
                    <div className="w-3 h-3 bg-[#B80E28] rounded-full ring-4 ring-[#B80E28]/20"></div>
                  </div>
                  <div className="flex-1 hidden md:block"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Responsible Gaming ── */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <SectionBadge text="Responsible Gaming" />
            <h2 className="text-white text-3xl md:text-4xl font-bold mb-4">We Care About Your Well-Being</h2>
            <p className="text-white/50 text-sm max-w-xl mx-auto">
              PNGGO is committed to promoting responsible gambling. We provide tools and resources to help you stay in control and enjoy gaming safely.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
            {responsibleItems.map((item, i) => (
              <div key={i} className="bg-[#111] border border-white/5 hover:border-[#B80E28]/30 rounded-xl p-6 text-center transition-all duration-300 group">
                <div className="w-14 h-14 flex items-center justify-center bg-[#B80E28]/15 rounded-full mx-auto mb-4 group-hover:bg-[#B80E28]/25 transition-colors">
                  <i className={`${item.icon} text-[#B80E28] text-2xl`}></i>
                </div>
                <h3 className="text-white font-bold text-sm mb-2">{item.title}</h3>
                <p className="text-white/50 text-xs leading-5">{item.desc}</p>
              </div>
            ))}
          </div>
          <div className="bg-[#B80E28]/10 border border-[#B80E28]/20 rounded-xl p-6 text-center">
            <i className="ri-alert-line text-[#B80E28] text-2xl mb-3 block"></i>
            <p className="text-white/70 text-sm leading-6">
              <strong className="text-white">Gambling should be fun.</strong> If you feel that gambling is affecting your life negatively, please seek help.
              PNGGO strictly prohibits players under 21 years of age. Play responsibly.
            </p>
          </div>
        </div>
      </section>

      {/* ── Payment Methods ── */}
      <section className="bg-[#0d0d0d] py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-10">
            <SectionBadge text="Banking" />
            <h2 className="text-white text-3xl font-bold mb-3">Fast & Secure Payments</h2>
            <p className="text-white/50 text-sm max-w-lg mx-auto">
              Deposit and withdraw in PGK through PNGGO&apos;s banking partners — <strong className="text-white/70">Kina Bank</strong> and{' '}
              <strong className="text-white/70">BSP</strong> — using online banking and mobile banking where supported. Most deposits credit quickly; withdrawals follow each bank&apos;s processing times.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-xl mx-auto">
            {paymentMethods.map((pm, i) => (
              <div key={i} className="bg-[#1a1a1a] border border-white/5 hover:border-[#B80E28]/30 rounded-xl p-5 text-center transition-all duration-300 group cursor-pointer">
                <div className="w-10 h-10 flex items-center justify-center mx-auto mb-3">
                  <i className={`${pm.icon} text-[#B80E28] text-2xl`}></i>
                </div>
                <span className="text-white/70 text-xs font-medium">{pm.name}</span>
              </div>
            ))}
          </div>
          <div className="mt-8 flex flex-wrap justify-center gap-6 text-white/30 text-xs">
            <span className="flex items-center gap-2"><i className="ri-time-line text-[#B80E28]"></i> Deposits: Instant</span>
            <span className="flex items-center gap-2"><i className="ri-time-line text-[#B80E28]"></i> Withdrawals: Within 5 Minutes</span>
            <span className="flex items-center gap-2"><i className="ri-money-dollar-circle-line text-[#B80E28]"></i> Currency: PGK</span>
            <span className="flex items-center gap-2"><i className="ri-shield-check-line text-[#B80E28]"></i> 100% Secure</span>
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <SectionBadge text="Get Started" />
          <h2 className="text-white text-3xl md:text-4xl font-bold mb-5">
            Ready to Experience<br />
            <span className="text-[#B80E28]">PNG&apos;s Best Casino?</span>
          </h2>
          <p className="text-white/55 text-sm leading-7 mb-8 max-w-xl mx-auto">
            Join over 100,000 players across Papua New Guinea who trust PNGGO for their online gaming entertainment. Sign up today and claim your welcome bonus.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a
              href="https://pnggo.bet/RFLANDINGPAGEPNG"
              className="inline-flex items-center gap-2 bg-[#B80E28] text-white font-bold px-10 py-3.5 rounded-full hover:bg-[#9a0b21] transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              Create Free Account <i className="fas fa-chevron-right text-xs"></i>
            </a>
            <a
              href="/"
              className="inline-flex items-center gap-2 border border-white/20 text-white font-semibold px-10 py-3.5 rounded-full hover:border-[#B80E28] hover:text-[#B80E28] transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              Back to Home
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
